/**
 * Language Context
 * 
 * Provides language and translation functionality throughout the application.
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import { es } from './languages/es';
import { pt } from './languages/pt';

// Available languages
const LANGUAGES = {
  es,
  pt,
};

// Default language
const DEFAULT_LANGUAGE = 'es';

// Local storage key
const LANGUAGE_STORAGE_KEY = 'app_language';

/**
 * Detect browser/device language
 * @returns {string} Detected language code or default
 */
const detectBrowserLanguage = () => {
  try {
    // Get browser language (e.g., 'es-ES', 'pt-BR', 'en-US')
    const browserLang = navigator.language || navigator.userLanguage;

    if (browserLang) {
      // Extract the primary language code (e.g., 'es' from 'es-ES')
      const langCode = browserLang.split('-')[0].toLowerCase();

      // Check if we support this language
      if (LANGUAGES[langCode]) {
        console.log(`Detected browser language: ${langCode}`);
        return langCode;
      }
    }
  } catch (error) {
    console.warn('Failed to detect browser language:', error);
  }

  return DEFAULT_LANGUAGE;
};

// Create context
const LanguageContext = createContext(undefined);

/**
 * Language Provider Component
 * Wraps the application to provide language functionality
 */
export function LanguageProvider({ children }) {
  // Get initial language from localStorage, browser detection, or default
  const getInitialLanguage = () => {
    try {
      // First, check localStorage (user's explicit choice)
      const savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (savedLanguage && LANGUAGES[savedLanguage]) {
        console.log(`Using saved language: ${savedLanguage}`);
        return savedLanguage;
      }

      // If no saved language, detect from browser
      const detectedLanguage = detectBrowserLanguage();
      if (detectedLanguage !== DEFAULT_LANGUAGE) {
        // Save the detected language
        localStorage.setItem(LANGUAGE_STORAGE_KEY, detectedLanguage);
      }
      return detectedLanguage;
    } catch (error) {
      console.warn('Failed to load language from localStorage:', error);
      return DEFAULT_LANGUAGE;
    }
  };

  const [currentLanguage, setCurrentLanguage] = useState(getInitialLanguage);

  // Get current translations
  const translations = LANGUAGES[currentLanguage] || LANGUAGES[DEFAULT_LANGUAGE];

  /**
   * Change the current language
   * @param {string} languageCode - Language code (e.g., 'es', 'pt')
   */
  const changeLanguage = useCallback((languageCode) => {
    if (LANGUAGES[languageCode]) {
      setCurrentLanguage(languageCode);
      try {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, languageCode);
      } catch (error) {
        console.warn('Failed to save language to localStorage:', error);
      }
    } else {
      console.warn(`Language '${languageCode}' not found. Available languages:`, Object.keys(LANGUAGES));
    }
  }, []);

  /**
   * Get a translated string by path
   * @param {string} path - Dot-notation path to translation (e.g., 'common.welcome')
   * @param {object} params - Optional parameters to interpolate into the translation
   * @returns {string} Translated string
   */
  const t = useCallback((path, params = {}) => {
    // Split path by dots
    const keys = path.split('.');

    // Navigate through the translations object
    let value = translations;
    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        console.warn(`Translation not found for path: ${path}`);
        return path; // Return the path if translation not found
      }
    }

    // If the value is an object or array, return it directly
    if (typeof value === 'object') {
      return value;
    }

    // If the value is not a string, warn but return it
    if (typeof value !== 'string') {
      console.warn(`Translation at path ${path} is not a string:`, value);
      return value;
    }

    // Interpolate parameters if provided
    let result = value;
    Object.keys(params).forEach(key => {
      const placeholder = `{${key}}`;
      result = result.replace(new RegExp(placeholder, 'g'), params[key]);
    });

    return result;
  }, [translations]);

  /**
   * Get available languages
   * @returns {Array} Array of language objects with code and name
   */
  const getAvailableLanguages = useCallback(() => {
    return Object.keys(LANGUAGES).map(code => ({
      code,
      name: LANGUAGES[code].language.name,
      flag: LANGUAGES[code].language.flag,
    }));
  }, []);

  /**
   * Check if a language is available
   * @param {string} languageCode - Language code to check
   * @returns {boolean}
   */
  const isLanguageAvailable = useCallback((languageCode) => {
    return languageCode in LANGUAGES;
  }, []);

  // Context value
  const value = {
    currentLanguage,
    changeLanguage,
    t,
    translations,
    getAvailableLanguages,
    isLanguageAvailable,
    languages: LANGUAGES,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

/**
 * Hook to use language context
 * @returns {object} Language context value
 */
export function useLanguage() {
  const context = useContext(LanguageContext);

  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }

  return context;
}

/**
 * Hook to get translation function
 * @returns {function} Translation function
 */
export function useTranslation() {
  const { t } = useLanguage();
  return t;
}

export default LanguageContext;
